// pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Skeleton,
  Paper,
  Fade,
  Grow,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Avatar,Snackbar,Alert
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Restaurant as RestaurantIcon,
  DeliveryDining as DeliveryIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  LocalOffer as OfferIcon,
  EmojiEvents as AwardIcon,
  Google as GoogleIcon,
  Store as StoreIcon,
  Storefront as StorefrontIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  WatchLater as WatchLaterIcon
} from '@mui/icons-material';
import WelcomePopup from '../components/WelcomePopup';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GoogleMapModal from '../components/GoogleMapModal';
import currencyTrans from '../components/CarrancyTrans';
import { useAuth } from '../context/AuthContext';
import noPhoto from "../assets/no_photo.webp";

const HomePage = ({ keywordsData, language }) => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { user, login } = useAuth();
  
  // Location states
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [isSelectMap, setIsSelectMap] = useState(false);
  const [openWelcomeModel, setOpenWelcomeModel] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Restaurant states
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cuisineTypes, setCuisineTypes] = useState([]);
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [gpsChecking, setGpsChecking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

// Add Snackbar close handler
const handleCloseSnackbar = () => {
  setSnackbar({ ...snackbar, open: false });
};
  // Recently viewed stores
  const [recentStores, setRecentStores] = useState([]);

  // Load recently viewed stores from localStorage
  useEffect(() => {
    const savedRecentStores = localStorage.getItem('recent_stores');
    if (savedRecentStores) {
      try {
        const parsed = JSON.parse(savedRecentStores);
        setRecentStores(parsed.slice(0, 10)); // Max 10 stores
      } catch (e) {
        console.error('Error loading recent stores:', e);
      }
    }
  }, []);

  // Save store to recently viewed
  const addToRecentStores = (store) => {
    setRecentStores(prev => {
      // Remove if already exists
      const filtered = prev.filter(s => s.id !== store.id);
      // Add to beginning
      const newRecent = [store, ...filtered].slice(0, 10);
      // Save to localStorage
      localStorage.setItem('recent_stores', JSON.stringify(newRecent));
      return newRecent;
    });
  };

  // Clear recently viewed stores
  const clearRecentStores = () => {
    setRecentStores([]);
    localStorage.removeItem('recent_stores');
  };

  // Remove single store from recent
  const removeFromRecent = (storeId, e) => {
    e.stopPropagation();
    setRecentStores(prev => {
      const newRecent = prev.filter(s => s.id !== storeId);
      localStorage.setItem('recent_stores', JSON.stringify(newRecent));
      return newRecent;
    });
  };

  // Google Login handler
  const handleGoogleLogin = async (response) => {
    setGoogleLoading(true);
    try {
      const res = await api.post('/auth/google', {
        credential: response.credential,
        client_id: '387866063920-1bfr2kigso4o76ft4nh2fr3i5m61elee.apps.googleusercontent.com'
      });
      
      if (res.data.success) {
        const { token, user } = res.data;
        await login(token, user);
      }
    } catch (error) {
      console.error('Google login error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  // Initialize Google One-Tap
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && !user) {
        window.google.accounts.id.initialize({
          client_id: '387866063920-1bfr2kigso4o76ft4nh2fr3i5m61elee.apps.googleusercontent.com',
          callback: handleGoogleLogin,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin'
        });
        
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.log('One-tap not displayed:', notification.getNotDisplayedReason());
          }
        });
      }
    };
    document.body.appendChild(script);
    
    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
      document.body.removeChild(script);
    };
  }, [user]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Google Maps API
  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = process.env.REACT_APP_MAP_API_KEY;
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };

  // Fetch places for autocomplete
  const fetchPlaces = async (input) => {
    if (isSelectMap) return;
    
    try {
      const response = await api.get(`/autocomplete?input=${input}&language=${language}`);
      const data = response.data;
      if (data.predictions) {
        setSuggestions(data.predictions.map((place) => place.description));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  // Handle location selection from search
  const handleLocationSelect = (locationData) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(locationData));
      return;
    }

    if (locationData.address) {
      setQuery(locationData.address);
      localStorage.setItem('user_location', JSON.stringify(locationData));
      setLocation({
        lat: locationData.lat,
        lng: locationData.lng,
        address: locationData.address
      });
      fetchNearbyRestaurants(locationData.lat, locationData.lng);
      setIsSelectMap(true);
      setShowSuggestions(false);
    } else {
      setQuery(locationData);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationData }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          setSelectedLocation({
            lat: loc.lat(),
            lng: loc.lng(),
            address: locationData,
          });
          setOpenMap(true);
        }
      });
      setShowSuggestions(false);
    }
    setSuggestions([]);
  };

const checkGpsAndOpenMap = () => {
  // Check if geolocation is supported
  if (!navigator.geolocation) {
    setSnackbar({
      open: true,
      message: language === 'ar' 
        ? 'المتصفح الخاص بك لا يدعم تحديد الموقع الجغرافي'
        : 'Your browser does not support geolocation',
      severity: 'error'
    });
    return;
  }

  // Show loading indicator
  setGpsChecking(true);
  
  // Try to get current position to check if GPS is enabled
  navigator.geolocation.getCurrentPosition(
    (position) => {
      // GPS is enabled - proceed to open map
      setGpsChecking(false);
      setSelectedLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setOpenMap(true);
    },
    (error) => {
      setGpsChecking(false);
      
      // Handle different error cases
      let errorMessage = '';
      let severity = 'warning';
      
      switch(error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = language === 'ar'
            ? '⚠️ تم رفض الوصول إلى الموقع. يرجى تمكين خدمات الموقع في إعدادات هاتفك أو المتصفح لتحديد موقعك بدقة'
            : '⚠️ Location access denied. Please enable location services in your phone or browser settings for accurate location detection';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = language === 'ar'
            ? '⚠️ معلومات الموقع غير متوفرة. يرجى التحقق من إعدادات GPS في هاتفك'
            : '⚠️ Location information unavailable. Please check your phone\'s GPS settings';
          break;
        case error.TIMEOUT:
          errorMessage = language === 'ar'
            ? '⚠️ انتهى وقت محاولة تحديد الموقع. يرجى المحاولة مرة أخرى'
            : '⚠️ Location request timed out. Please try again';
          break;
        default:
          errorMessage = language === 'ar'
            ? '⚠️ يرجى تمكين خدمات الموقع (GPS) لتحديد موقعك الحالي بدقة'
            : '⚠️ Please enable location services (GPS) to detect your current location accurately';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: severity
      });
    },
    { 
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    }
  );
};

// Replace your existing handleAutoDetect with this
const handleAutoDetect = () => {
  checkGpsAndOpenMap();
};


  // Handle search input change
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    clearTimeout(window.debounceTimeout);
    window.debounceTimeout = setTimeout(() => {
      if (value.length > 2) {
        fetchPlaces(value);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchNearbyRestaurants = async (lat, lng) => {
    try {
      setLoading(true);
      const params = { lat, lng, radius: 10, sortBy: 'distance', page: 1, limit: 20 };
      const response = await api.get('/restaurants/nearby', { params });
      
      if (response.data.success) {
        setNearbyRestaurants(response.data.restaurants || []);
      }
    } catch (error) {
      console.error('Error fetching nearby restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToShopOwner = () => {
    navigate('/shop-owner-request');
  };

  // Handle restaurant click - save to recent stores
  const handleRestaurantClick = (restaurant) => {
    // Save to recent stores
    const storeData = {
      id: restaurant.id,
      name: restaurant.name,
      name_en: restaurant.name_en,
      logo: restaurant.logo || restaurant.cover_image,
      cuisine_type: restaurant.cuisine_type,
      rating: restaurant.rating
    };
    addToRecentStores(storeData);
    navigate(`/restaurant/${restaurant.id}`);
  };

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const savedLocation = localStorage.getItem('user_location');
        if (savedLocation) {
          const loc = JSON.parse(savedLocation);
          setLocation(loc);
          setQuery(loc.address);
          fetchNearbyRestaurants(loc.lat, loc.lng);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Responsive grid settings
  const getGridSize = () => {
    if (isMobile) return 12;
    if (isTablet) return 6;
    return 4;
  };

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', pb: { xs: 8, md: 10 } }}>
      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
        borderBottom: '2px solid #FFD700',
        py: { xs: 4, sm: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center' }}>
            <Grow in timeout={1000}>
              <Typography 
                variant="h1" 
                sx={{ 
                  color: '#FFD700',
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  fontWeight: 900,
                  mb: 2,
                  textShadow: '0 2px 20px rgba(255, 215, 0, 0.3)',
                  letterSpacing: '1px'
                }}
              >
                {language === 'ar' ? 'بائع أم شاري؟' : 'Buyer or Seller?'}
              </Typography>
            </Grow>
            
            <Fade in timeout={1500}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#FFFFFF',
                  fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' },
                  fontWeight: 500,
                  mb: 3,
                  opacity: 0.9,
                  px: 2
                }}
              >
                {language === 'ar' 
                  ? 'دعنا نجد لك أفضل المتاجر القريبة' 
                  : "Let's find you the best store nearby"}
              </Typography>
            </Fade>

            {/* Search Bar */}
            <Box ref={searchRef} sx={{ position: 'relative', maxWidth: '500px', mx: 'auto', px: { xs: 2, sm: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, position: 'relative' }}>
                  <input
                    id='area_search'
                    name='area_search'
                    className="search-input"
                    type="text"
                    placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                    value={query}
                    onChange={handleQueryChange}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  />
                </Box>
                <button className="location-btn" onClick={handleAutoDetect}>
                  <MyLocationIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </button>
              </Box>

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <Fade in>
                  <Box className="suggestions-dropdown">
                    {suggestions.map((place, index) => (
                      <Box
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleLocationSelect(place)}
                      >
                        <LocationIcon sx={{ fontSize: 18, color: '#FFD700', ml: language === 'ar' ? 1 : 0, mr: language === 'ar' ? 0 : 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{place}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', px: 2 }}>
              <Button
                variant="contained"
                onClick={() => setOpenWelcomeModel(true)}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  '&:hover': { backgroundColor: '#FFE55C', transform: 'translateY(-2px)' },
                  transition: 'all 0.2s ease',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 0.8, sm: 1 },
                  borderRadius: '50px',
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                }}
              >
                {language === 'ar' ? 'كن شريكاً' : 'Become a Partner'}
              </Button>
              
              {!user && (
                <Button
                  variant="outlined"
                  startIcon={googleLoading ? <CircularProgress size={18} /> : <GoogleIcon />}
                  disabled={googleLoading}
                  onClick={() => window.google?.accounts?.id?.prompt()}
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    '&:hover': {
                      borderColor: '#FFE55C',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 0.8, sm: 1 },
                    borderRadius: '50px',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }}
                >
                  {language === 'ar' ? 'تسجيل الدخول بجوجل' : 'Sign in with Google'}
                </Button>
              )}
              {(user && user.role === 'user') && (
                <Button
                  variant="outlined"
                  startIcon={<StorefrontIcon />}
                  onClick={handleUpgradeToShopOwner}
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    '&:hover': {
                      borderColor: '#FFE55C',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 0.8, sm: 1 },
                    borderRadius: '50px',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }}
                >
                  {language === 'ar' ? 'الترقية إلى حساب متجر' : 'Upgrade to store account'}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* Recently Viewed Stores Section */}
        {recentStores.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLaterIcon sx={{ color: '#FFD700', fontSize: { xs: 20, sm: 24 } }} />
                <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                  {language === 'ar' ? 'المتاجر التي زرتها مؤخراً' : 'Recently Viewed Stores'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={clearRecentStores}
                sx={{ color: '#B3B3B3', '&:hover': { color: '#FF3D00' } }}
              >
                {language === 'ar' ? 'مسح الكل' : 'Clear All'}
              </Button>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              overflowX: 'auto', 
              pb: 2,
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-track': {
                background: '#1A1A1A',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#FFD700',
                borderRadius: 3,
              }
            }}>
              {recentStores.map((store) => (
                <Card
                  key={store.id}
                  onClick={() => navigate(`/restaurant/${store.id}`)}
                  sx={{
                    minWidth: { xs: 140, sm: 160 },
                    maxWidth: { xs: 140, sm: 160 },
                    cursor: 'pointer',
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2D2D2D',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: '#FFD700',
                    }
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => removeFromRecent(store.id, e)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: '#B3B3B3',
                      '&:hover': {
                        backgroundColor: '#FF3D00',
                        color: '#FFFFFF'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  
                  <Box sx={{ p: 1.5 }}>
                    <Avatar
                      src={store.logo}
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 1,
                        border: '2px solid #FFD700',
                        backgroundColor: '#0D0D0D'
                      }}
                    >
                      <StoreIcon sx={{ fontSize: 40, color: '#FFD700' }} />
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#FFFFFF', 
                        fontWeight: 600,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {language === 'ar' ? store.name : store.name_en || store.name}
                    </Typography>
                    {store.rating && (
                      <Chip
                        label={`${store.rating} ⭐`}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          height: 20,
                          fontSize: '0.65rem',
                          width: 'fit-content',
                          mx: 'auto'
                        }}
                      />
                    )}
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Location Header */}
        {location && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <LocationIcon sx={{ color: '#FFD700', fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'موقعك:' : 'Your location:'}
            </Typography>
            <Chip
              label={location.address}
              size="small"
              sx={{ bgcolor: '#1A1A1A', color: '#FFD700', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          </Box>
        )}

        {/* Restaurants Grid */}
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={280} sx={{ bgcolor: '#1A1A1A', borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : nearbyRestaurants.length > 0 ? (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {nearbyRestaurants.map((restaurant) => (
              <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                <RestaurantCard 
                  restaurant={restaurant} 
                  onClick={() => handleRestaurantClick(restaurant)}
                  language={language}
                  isMobile={isMobile}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ 
            p: { xs: 4, sm: 6 }, 
            textAlign: 'center', 
            bgcolor: '#0D0D0D',
            border: '2px dashed #2D2D2D',
            borderRadius: 4
          }}>
            <StoreIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#2D2D2D', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#B3B3B3', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {language === 'ar' 
                ? 'لا توجد متاجر في هذه المنطقة بعد' 
                : 'No stores in this area yet'}
            </Typography>
          </Paper>
        )}
      </Container>
      
      <WelcomePopup isOpen={openWelcomeModel} isPart={true} onClose={() => setOpenWelcomeModel(false)} />
      
      {/* Google Maps Modal */}
      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={(location) => handleLocationSelect(location)}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />

            {/* Snackbar for notifications */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={5000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity={snackbar.severity}
                sx={{
                  backgroundColor: '#0D0D0D',
                  color: '#FFFFFF',
                  border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : snackbar.severity === 'warning' ? '#FF9800' : '#F44336'}`,
                  borderRadius: '12px'
                }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>

      {/* Global Styles */}
      <style jsx global>{`
        .search-input {
          width: 100%;
          padding: 12px 16px;
          font-size: 16px;
          background-color: #1A1A1A;
          border: 2px solid #2D2D2D;
          border-radius: 50px;
          color: #FFFFFF;
          outline: none;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #FFD700;
          box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
        }
        
        .search-input::placeholder {
          color: #6B6B6B;
        }
        
        .location-btn {
          background-color: #FFD700;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .location-btn:hover {
          background-color: #FFE55C;
          transform: scale(1.05);
        }
        
        .location-btn svg {
          color: #000000;
        }
        
        .suggestions-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background-color: #1A1A1A;
          border: 1px solid #2D2D2D;
          border-radius: 16px;
          margin-top: 8px;
          max-height: 300px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        
        .suggestion-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          color: #FFFFFF;
        }
        
        .suggestion-item:hover {
          background-color: rgba(255, 215, 0, 0.1);
        }
        
        @media (max-width: 600px) {
          .search-input {
            padding: 10px 14px;
            font-size: 14px;
          }
          
          .location-btn {
            width: 42px;
            height: 42px;
          }
          
          .suggestion-item {
            padding: 10px 14px;
          }
        }
      `}</style>
    </Box>
  );
};

const RestaurantCard = ({ restaurant, onClick, language, isMobile }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const isOpen = restaurant.menu_settings?.is_open ?? true;
  const openStatusText = isOpen 
    ? (language === 'ar' ? 'مفتوح' : 'Open') 
    : (language === 'ar' ? 'مغلق' : 'Closed');
  const openStatusColor = isOpen ? '#4CAF50' : '#FF3D00';

  return (
    <Card 
      onClick={onClick}
      sx={{
        bgcolor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: '#FFD700',
          boxShadow: '0 8px 24px rgba(255, 215, 0, 0.15)',
        }
      }}
    >
      {/* Status Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: `${openStatusColor}`,
          padding: '4px 10px',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
          }}
        />
        <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.7rem' }}>
          {openStatusText}
        </Typography>
      </Box>

      {/* Image */}
      <Box sx={{ position: 'relative', height: { xs: 160, sm: 180 }, overflow: 'hidden' }}>
        {/* Loading Skeleton */}
        {!imageLoaded && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#1A1A1A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid #2D2D2D',
                borderTopColor: '#FFD700',
                animation: 'spin 0.8s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        )}
        
        <CardMedia
          component="img"
          image={!imageError ? (restaurant.cover_image || restaurant.logo || noPhoto) : noPhoto}
          alt={restaurant.name}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          sx={{ 
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />

        {/* Rating Badge */}
        <Chip
          label={`${restaurant.rating || '4.5'} ⭐`}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: '#FFD700',
            color: '#000000',
            fontWeight: 700,
            zIndex: 2
          }}
        />

        {/* Distance Badge */}
        {restaurant.distance && (
          <Chip
            label={`${restaurant.distance.toFixed(1)} km`}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: '#FFD700',
              fontWeight: 600,
              backdropFilter: 'blur(4px)',
              zIndex: 2
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flex: 1 }}>
        <Typography variant="h6" sx={{ 
          color: '#FFFFFF', 
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: '0.95rem', sm: '1rem' },
          lineHeight: 1.3,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {language === 'ar' ? restaurant.name : restaurant.name_en || restaurant.name}
        </Typography>
        
        {restaurant.cuisine_type && (
          <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 1 }}>
            {language === 'ar' ? restaurant.cuisine_type : restaurant.cuisine_type_en || restaurant.cuisine_type}
          </Typography>
        )}
        
        {/* Delivery Info */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ fontSize: 14, color: '#FFD700' }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
              {restaurant.delivery_time_min || '25-35'} min
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DeliveryIcon sx={{ fontSize: 14, color: '#FFD700' }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
              {restaurant.delivery_fee || '5'} {currencyTrans(restaurant.currency, language)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" sx={{ color: '#6B6B6B', display: 'block' }}>
          {language === 'ar' ? 'الحد الأدنى: ' : 'Min: '}
          {restaurant.min_order_amount || '20'} {currencyTrans(restaurant.currency, language)}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default HomePage;